import { useEffect, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import { TestStatus } from '@dbt-labs/dbt-dag';
import {
  CodeSnippet,
  RyeconClipboardSuccess,
  RyeconClock,
  RyeconInfoOutline,
  RyeconRerun,
  RyeconTable,
  RyeconTableColumn,
  RyeconTest,
  Tooltip,
} from '@dbt-labs/sourdough';

import { Maybe } from '@/__generated__/graphql';
import DetailTabs, { TabInfo, TabType } from '@/components/DetailTabs';
import { DetailsSection } from '@/components/SectionWithCard';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useMissingDataHeader } from '@/context/MissingDataHeaderProvider';
import { useTest } from '@/hooks/dbtStrategy/useTest';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { useValidatedParams } from '@/hooks/params';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { isDevelopment } from '@/typings/url';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import { parseTestName, translateTestType } from '@/util/dbt/testName';
import {
  formattedExpectStringArray,
  formattedGivenStringArray,
} from '@/util/dbt/yamlFormatter';
import { toTitleCase } from '@/util/string';
import { ResourceDetailsHeader, TextIconPair } from '../Header';
import { ExecutionInfo } from '../Sections/RecentSection';
import { CodeView } from '../Tabs/CodeView';
import { GeneralView } from '../Tabs/GeneralView';
import { PerformanceView } from '../Tabs/PerformanceView';

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const TestView = ({ uniqueId }: ResourceParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { environmentType } = useValidatedParams();
  const resourceQuery = useTest(uniqueId);
  const resource = resourceQuery.fetching ? null : resourceQuery.data;
  const hasPerformanceTab = hasAccountFeature('explorerModelPerformance');

  const tabs = useMemo((): TabInfo[] => {
    const base: TabInfo[] = [{ type: 'general' }, { type: 'code' }];
    if (!isDevelopment(environmentType) && hasPerformanceTab) {
      base.push({ type: 'performance' as TabType });
    }
    return base;
  }, [environmentType, hasPerformanceTab]);
  const { setMissingTestResults } = useMissingDataHeader();
  const { updatedAt, updatedAtUtc, testType, date } = useMemo(() => {
    const date = resource?.applied?.executionInfo?.executeCompletedAt
      ? new Date(resource.applied.executionInfo.executeCompletedAt)
      : undefined;
    const testType = translateTestType(resource?.applied?.testType || null);
    const updatedAt = date && formatAbsoluteLocalDate(date);
    const updatedAtUtc = date && formatDateCommon(date).utc;
    return { updatedAt, updatedAtUtc, testType, date };
  }, [resource?.applied]);
  const lastKnownResult = resource?.applied?.lastKnownResult ?? null;
  const status = (resource?.applied?.executionInfo?.lastRunStatus ??
    null) as Maybe<TestStatus>;
  const statusTooltip = resource?.applied?.executionInfo?.lastRunError ?? undefined;
  const parsedName = useMemo(
    () => (resource?.applied ? parseTestName(resource.applied) : undefined),
    [resource?.applied],
  );

  const headerIcons = useMemo((): TextIconPair[] => {
    const icons: TextIconPair[] = [];
    if (testType) {
      icons.push({
        ryecon: RyeconClipboardSuccess,
        text: `${testType} Test`,
        tooltip: 'Test type',
      });
    }
    if (updatedAt) {
      icons.push({
        ryecon: RyeconClock,
        text: `Last run ${updatedAt}`,
        tooltip: updatedAtUtc || undefined,
      });
    }
    if (lastKnownResult) {
      icons.push({
        ryecon: RyeconTest,
        text: `Test result: ${lastKnownResult ? toTitleCase(lastKnownResult) : 'Unknown'}`,
      });
    }
    if (status) {
      const classNames = twJoin(
        status === 'pass' && 'text-fgSuccess',
        (status === 'error' || status === 'fail') && 'text-fgDanger',
        status === 'warn' && 'text-fgWarning',
        status === 'reused' && 'text-fgVizTeal',
        status === 'skipped' && '',
      );
      icons.push({
        ryecon: RyeconRerun,
        key: 'status',
        Contents: () => (
          <span>
            <span>Latest run status: </span>
            <span className={`${classNames} inline-block`}>
              <Tooltip content={statusTooltip}>
                {toTitleCase(status.toString())}
              </Tooltip>
            </span>
          </span>
        ),
      });
    }

    if (parsedName && (parsedName.ref || parsedName.columnName)) {
      icons.push({
        ryecon: RyeconInfoOutline,
        text: parsedName.shortName,
        tooltip: 'Test name',
      });
      if (parsedName.ref) {
        icons.push({
          ryecon: RyeconTable,
          text: parsedName.ref,
          tooltip: 'Test target',
        });
      }
      if (parsedName.columnName) {
        icons.push({
          ryecon: RyeconTableColumn,
          text: parsedName.columnName,
          tooltip: 'Column name',
        });
      }
    }
    return icons;
  }, [
    parsedName,
    testType,
    updatedAt,
    status,
    updatedAtUtc,
    statusTooltip,
    lastKnownResult,
  ]);

  const applied = resource?.applied;
  useEffect(() => {
    const isMissingTestData = !!applied && applied.executionInfo?.lastRunStatus == null;
    setMissingTestResults(isMissingTestData);
    return () => {
      setMissingTestResults(false);
    };
  }, [setMissingTestResults, applied]);

  const result = useMemo(
    () => formattedGivenStringArray(resource?.applied?.given || []),
    [resource?.applied?.given],
  );

  const expectResult = useMemo(
    () => formattedExpectStringArray(resource?.applied?.expect || []),
    [resource?.applied?.expect],
  );

  if (resourceQuery.error) return <>{resourceQuery.error.stack}</>;
  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={applied?.name ?? ''}
        headerIcons={headerIcons}
        packageName={uniqueId.packageName}
        files={[applied?.patchPath, applied?.filePath]}
        name={applied?.name}
        resourceType="test"
        updatedDate={date}
      />
      <DetailTabs tabs={tabs} show={!!resource}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <GeneralView
                  resource={resource?.applied as GenericResourceNode}
                  uniqueId={uniqueId}
                  description={resource?.applied?.description}
                  meta={resource?.applied?.meta}
                />
              )}
              {activeTab === 'code' && testType === 'Data' && (
                <CodeView
                  rawCode={resource?.applied?.rawCode}
                  compiledCode={resource?.applied?.compiledCode}
                />
              )}

              {activeTab === 'code' && testType === 'Unit' && (
                <>
                  {result?.map((item, index) => (
                    <DetailsSection
                      key={index}
                      className="mt-4"
                      heading="Given"
                      isCompact
                    >
                      <CodeSnippet language="json" code={item} />
                    </DetailsSection>
                  ))}
                  <DetailsSection className="mt-4" heading="Expect" isCompact>
                    <CodeSnippet language="json" code={expectResult} />
                  </DetailsSection>
                </>
              )}
              {activeTab === 'performance' && (
                <PerformanceView
                  uniqueId={uniqueId.raw}
                  executionInfo={resource?.applied?.executionInfo as ExecutionInfo}
                  resourceType="test"
                />
              )}
            </>
          );
        }}
      </DetailTabs>
    </>
  );
};
